type SurfaceContrast = "dark" | "light";

/**
 * Text colour for each contrast, as theme colour paths. `light` is light text
 * for dark surfaces; `dark` is dark text for light surfaces.
 */
const CONTRAST_FOREGROUND_COLORS: Record<SurfaceContrast, string> = {
  dark: "new-beginnings.base",
  light: "white",
};

/**
 * The page surface a section sits on when it is not painted. `value` lets a
 * lead flow step or dialog paint it explicitly over a painted section.
 */
const UNPAINTED_SURFACE = {
  background: "white",
  contrast: "dark",
  value: "page",
} as const satisfies {
  background: string;
  contrast: SurfaceContrast;
  value: string;
};

/**
 * Surfaces a page builder section can be painted with. `value` is what Studio
 * stores and names the surface's role rather than its colour, so a theme can
 * repaint a surface without touching content; `title` names the brand colour
 * it currently paints. `background` is a theme colour path and `contrast`
 * picks the text colour that stays readable on it.
 */
const SECTION_SURFACES = [
  {
    background: "new-beginnings.base",
    contrast: "light",
    title: "Bold (New Beginnings)",
    value: "bold",
  },
  {
    background: "clear-sky.tints.500",
    contrast: "dark",
    title: "Calm (Clear Sky)",
    value: "calm",
  },
  {
    background: "support.tints.400",
    contrast: "dark",
    title: "Soft (Support)",
    value: "soft",
  },
  {
    background: "friendly.tints.500",
    contrast: "dark",
    title: "Warm (Friendly)",
    value: "warm",
  },
  {
    // TODO: This is a temporary surface for the campaign shell. If it needs
    // wider use, we should should consider updating the content background and
    // "Soft (support)" to use the new color.
    background: "campaign-shell",
    contrast: "dark",
    title: "Campaign",
    value: "campaign",
  },
  {
    background: "holiday",
    contrast: "light",
    title: "Holiday Bold (Holiday)",
    value: "holiday-bold",
  },
  {
    background: "clear-sky.tints.800",
    contrast: "dark",
    title: "Holiday Calm (Clear Sky light)",
    value: "holiday-calm",
  },
] as const satisfies ReadonlyArray<{
  background: string;
  contrast: SurfaceContrast;
  title: string;
  value: string;
}>;

/** Studio dropdown options for a section background. */
const SECTION_SURFACE_OPTIONS = SECTION_SURFACES.map(({ title, value }) => ({
  title,
  value,
}));

/**
 * The themes a section or lead flow step picks from: the page surface,
 * then every paintable surface. A theme fixes the background and its text
 * contrast together, so an author can't pair a background with text that
 * disappears on it.
 */
const THEMES = [
  { ...UNPAINTED_SURFACE, title: "Page (White)" },
  ...SECTION_SURFACES,
];

/** Studio options for a theme field. */
const THEME_OPTIONS = THEMES.map(({ title, value }) => ({ title, value }));

/** The text contrast a theme pairs with, or undefined for an unknown value. */
function getThemeContrast(
  theme: string | null | undefined,
): SurfaceContrast | undefined {
  return THEMES.find(({ value }) => value === theme)?.contrast;
}

export {
  CONTRAST_FOREGROUND_COLORS,
  getThemeContrast,
  SECTION_SURFACE_OPTIONS,
  SECTION_SURFACES,
  THEME_OPTIONS,
  THEMES,
  UNPAINTED_SURFACE,
};
